import React, {useState, useEffect, useRef} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useErrorSetter } from '../../../hooks/useUiMessages';
import {PageDataContainerStyled} from "../../../styled/Resume";
import Button from "../../UI/Buttons/Button";
import uploadBtnStyles from '../../UI/UploadPasteButton.module.css';
import DragAndDropSystem from "./DragAndDropSystem";
import {useTheme} from "../../UI/Theme/ThemeContext";
import InputHandler from "./InputHandler";
import { FaWandMagicSparkles, FaRegLightbulb } from "react-icons/fa6";
import { FiUpload } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import ResumeSelectList from "../ResumeSelectList";
import OvalSpinner from "../../UI/Spinners/OvalSpinner";

const uploadSwitcherStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    marginTop: '-15px',
};

const highlightYellow = '#EE7B1C';

const orBadgeStyle = {
    fontSize: '.7rem',
    letterSpacing: '0.35em',
    textTransform: 'uppercase',
    color: highlightYellow,
};

const inlineUploadButtonStyle = {
    borderRadius: 999,
    border: '1px solid rgba(62, 143, 147, 0.6)',
    background: 'linear-gradient(120deg, rgba(126, 240, 255, 0.2), rgba(62, 143, 147, 0.08))',
    color: '#0c1e28',
    padding: '6px 14px',
    fontSize: '.78rem',
    textTransform: 'uppercase',
    letterSpacing: '.05em',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(4, 35, 48, 0.18)',
};

const errorTextStyle = {
    marginTop: 6,
    color: '#c62828',
    fontSize: '.72rem',
    fontWeight: 600,
};

const deleteActionWrapperStyle = {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: -3,
    marginTop: -55,
    marginLeft: -5
};

const selectedBadgeWrapperBase = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 8,
    borderRadius: 12,
    padding: '8px 14px',
    background: 'rgba(255, 255, 255, 0.75)',
    border: '1px solid rgba(62, 143, 147, 0.35)',
    marginBottom: '10px',
    marginTop: '-10px'
};

const selectedBadgeLabelBase = {
    fontSize: '.72rem',
    textTransform: 'uppercase',
    letterSpacing: '.2em',
    color: '#0c1229',
};

const selectedBadgeValueBase = {
    flex: 1,
    fontWeight: 600,
    color: '#0d3f42',
    paddingLeft: 6,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const formatInterestsForTextarea = (list) => {
    if (!Array.isArray(list)) return '';
    return list.filter((entry) => typeof entry === 'string' && entry.trim().length).join('\n');
};

const parseInterestsFromText = (textValue) => {
    if (!textValue || typeof textValue !== 'string') return [];
    return textValue
        .split(/(?:\r?\n)+|[,;]+|\s[-–—]\s|[•·]/)
        .map((entry) => entry.trim())
        .filter(Boolean);
};

const OptimizePageData = (props) => {
    const {
        DragDropHandleDrop,
        DragDropRef,
        DragDropFileError,
        DragDropFile,
        DragDropHandleUploadFile,
        clearManualUpload,
        optimizeBasicInfo,
        optimizeHandleInputChange,
        optimizeJobConnectType,
        optimizeJobTypesArray,
        optimizeHandleJobConnectType,
        optimizeSetAdditionalInfo,
        optimizeAdditionalInfo,
        handleFileResumeOptimize,
        // optional image attachments support for additional info
        addlInfoImagePreviews,
        addlInfoFileInputRef,
        triggerAddlInfoSelect,
        handleAddlInfoFileSelect,
        removeSingleAddlInfoImage,
        existingResumes,
        existingResumeSelectedIndex,
        onExistingResumeSelect,
        onExistingResumePreview,
        storedResumeSelected,
        selectedStoredResumeName,
        interests,
        setInterestsArray,

    } = props;
    const [disableFields, setDisableFields] = useState(false);
    const [needsCareerGuidance, setNeedsCareerGuidance] = useState(false);
    const [guidanceCheckLoading, setGuidanceCheckLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [interestsTextValue, setInterestsTextValue] = useState(() => formatInterestsForTextarea(interests));
    const manualInterestSyncRef = useRef(false);
    const mountedRef = useRef(false);
    const {themeName} = useTheme();
    const safeSetInterestsArray = typeof setInterestsArray === 'function'
        ? setInterestsArray
        : () => {};
    const hasStoredResumes = Array.isArray(existingResumes) && existingResumes.length > 0;
    const selectLabelStyle = {
        color: highlightYellow,
        marginTop: 0,
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: '.18em',
        fontSize: '.68rem'
    };
    const tipSectionWrapperStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '12px 16px',
        borderRadius: 14,
        background: themeName === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 194, 120, 0.12)',
        border: `1px solid ${highlightYellow}`,
        marginBottom: 0,
    };
    const tipHeaderStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: highlightYellow,
        fontSize: '.82rem',
        fontWeight: 600,
    };
    const tipCheckboxWrapperStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        color: highlightYellow,
        fontWeight: 600,
        fontSize: '.78rem',
    };

    // Checkbox visual styles (force on iOS PWAs by removing native appearance
    // and rendering a consistent box with an inline SVG checkmark when checked)
    const checkboxBaseStyle = {
        WebkitAppearance: 'none',
        appearance: 'none',
        display: 'inline-block',
        width: 18,
        height: 18,
        minWidth: 18,
        minHeight: 18,
        borderRadius: 4,
        border: `2px solid ${highlightYellow}`,
        boxSizing: 'border-box',
        verticalAlign: 'middle',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        padding: 0,
        boxShadow: 'none',
        backgroundClip: 'padding-box',
        WebkitTapHighlightColor: 'transparent',
        accentColor: highlightYellow,
        outline: 'none',
    };

    const checkmarkSvg = encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='white' d='M20.285 6.708l-11.39 11.39-5.18-5.18 1.414-1.414 3.766 3.766 9.976-9.976z'/></svg>");

    const checkboxCheckedStyle = {
        backgroundColor: highlightYellow,
        borderColor: highlightYellow,
        boxShadow: 'none',
        backgroundImage: `url("data:image/svg+xml;utf8,${checkmarkSvg}")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '50% 50%',
        backgroundSize: '60% 60%',
    };
    const inlineUploadButtonDynamicStyle = {
        ...inlineUploadButtonStyle,
        color: themeName === 'dark' ? '#fff' : '#0c1e28',
        border: themeName === 'dark' ? '1px solid rgba(255, 255, 255, 0.45)' : inlineUploadButtonStyle.border,
        background: themeName === 'dark'
            ? 'linear-gradient(120deg, rgba(255,255,255,0.08), rgba(62, 143, 147, 0.25))'
            : inlineUploadButtonStyle.background,
    };
    const deleteButtonStyle = {
        borderRadius: '50%',
        width: 34,
        height: 34,
        border: '1px solid crimson',
        background: 'transparent',
        color: 'crimson',
        cursor: 'pointer',
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1rem',
        boxShadow: 'none',
        transition: 'transform 0.2s ease, background 0.2s ease'
    };
    const selectedBadgeWrapperStyle = {
        ...selectedBadgeWrapperBase,
        background: themeName === 'dark' ? 'rgba(7, 12, 35, 0.34)' : selectedBadgeWrapperBase.background,
        border: themeName === 'dark' ? '1px solid rgba(126, 240, 255, 0.35)' : selectedBadgeWrapperBase.border,
    };
    const selectedBadgeLabelStyle = {
        ...selectedBadgeLabelBase,
        color: themeName === 'dark' ? '#7ef0ff' : selectedBadgeLabelBase.color,
    };
    const selectedBadgeValueStyle = {
        ...selectedBadgeValueBase,
        color: themeName === 'dark' ? '#fde4d4' : selectedBadgeValueBase.color,
    };
    useEffect(() => {
        const primaryFieldReady = needsCareerGuidance
            ? Boolean(interestsTextValue?.trim())
            : Boolean(optimizeBasicInfo.jobPosition?.trim());

        const hasFileReady = Boolean(
            (DragDropFile || storedResumeSelected) &&
            primaryFieldReady &&
            optimizeBasicInfo.country?.trim() &&
            optimizeBasicInfo.city?.trim() &&
            optimizeJobConnectType?.trim()
        );
        setDisableFields(!hasFileReady);
    }, [
        DragDropFile,
        storedResumeSelected,
        optimizeBasicInfo.jobPosition,
        optimizeBasicInfo.country,
        optimizeBasicInfo.city,
        optimizeJobConnectType,
        needsCareerGuidance,
        interestsTextValue,
    ]);

    useEffect(() => {
        if (manualInterestSyncRef.current) {
            manualInterestSyncRef.current = false;
            return;
        }
        setInterestsTextValue(formatInterestsForTextarea(interests));
    }, [interests]);

    useEffect(() => {
        const handleViewportChange = () => {
            if (typeof window !== 'undefined') {
                setIsMobile(window.innerWidth <= 640);
            }
        };
        handleViewportChange();
        window.addEventListener('resize', handleViewportChange);
        return () => {
            window.removeEventListener('resize', handleViewportChange);
        };
    }, []);

    const handleClick = (e) => {
        if (e) e.preventDefault();
        if (true) {
            handleFileResumeOptimize({
                needsGuidance: needsCareerGuidance,
                guidanceInterests: interestsTextValue,
            });
        }
    };

    const handleInlineUploadClick = () => {
        try {
            if (DragDropRef && DragDropRef.current) {
                DragDropRef.current.click();
            }
        } catch (_) {}
    };

    const handleInterestsTextChange = (event) => {
        const {value} = event.target;
        manualInterestSyncRef.current = true;
        setInterestsTextValue(value);
        const parsedInterests = parseInterestsFromText(value);
        safeSetInterestsArray(parsedInterests.length ? parsedInterests : ['']);
    };

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const navigate = useNavigate();
    const errorSetter = useErrorSetter();
    const reduxUser = useSelector((state) => state.stateData.user);

    const handleGuidanceSelection = async (nextValue) => {
        if (nextValue === needsCareerGuidance || guidanceCheckLoading) return;
        if (!nextValue) {
            setNeedsCareerGuidance(false);
            return;
        }
        setGuidanceCheckLoading(true);
        try {
            const isAuth = localStorage?.getItem('token');
            if (!isAuth) {
                errorSetter('Sign in to request guidance');
                navigate('/popin?resume');
                return;
            }
            const userHasRoadmap = Boolean(reduxUser && reduxUser.careerRoadmap && Object.keys(reduxUser.careerRoadmap || {}).length > 0);
            if (userHasRoadmap) {
                errorSetter('Complete or delete your existing career blueprint before generating a new one.');
                return;
            }
            if (mountedRef.current) setNeedsCareerGuidance(true);
        } finally {
            if (mountedRef.current) setGuidanceCheckLoading(false);
        }
    };

    const handleTipBoxToggle = () => {
        handleGuidanceSelection(!needsCareerGuidance);
    };

    const handleTipKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleGuidanceSelection(!needsCareerGuidance);
        }
    };

    const handleGuidanceCheckboxChange = async (event) => {
        event.stopPropagation();
        handleGuidanceSelection(event.target.checked);
    };

    const submitDisabled = disableFields;
    const showSelector = hasStoredResumes && !DragDropFile;
    const showSelectedBadge = storedResumeSelected && Boolean(selectedStoredResumeName);

    return (
        <PageDataContainerStyled theme={themeName}>
            <p className="infoText">Have an old resume? This option will help you optimize it to ATS and industry
                standards in seconds, with the right keywords and metrics</p>
            <div className="contentSection">
                <div className="dragDropSection">
                    {showSelector ? (
                        <>
                            <ResumeSelectList
                                label="Select a resume to optimize"
                                labelStyle={selectLabelStyle}
                                resumes={existingResumes}
                                selectedIndex={existingResumeSelectedIndex}
                                onSelectResume={onExistingResumeSelect}
                                onPreviewResume={onExistingResumePreview}
                                maxHeight={180}
                                containerStyle={{ minHeight: 180, marginTop: 0 }}
                                dataTest="optimize-resume-select"
                            />
                            {showSelectedBadge && (
                                <div style={selectedBadgeWrapperStyle}>
                                    <span style={selectedBadgeLabelStyle}>Attached</span>
                                    <span style={selectedBadgeValueStyle} title={selectedStoredResumeName}>
                                        {selectedStoredResumeName}
                                    </span>
                                    <button
                                        type="button"
                                        style={deleteButtonStyle}
                                        onClick={clearManualUpload}
                                        title="Remove attached resume"
                                    >
                                        <MdDelete size={18} />
                                    </button>
                                </div>
                            )}
                            <div style={uploadSwitcherStyle}>
                                <span style={orBadgeStyle}>OR</span>
                                <button
                                    type="button"
                                    style={inlineUploadButtonDynamicStyle}
                                    onClick={handleInlineUploadClick}
                                >
                                    <FiUpload style={{ marginRight: 6 }} /> Upload Resume
                                </button>
                            </div>
                            <input
                                type="file"
                                accept=".doc,.docx,.pdf,.txt"
                                style={{ display: 'none' }}
                                ref={DragDropRef}
                                onChange={DragDropHandleUploadFile}
                            />
                        </>
                    ) : (
                        <>
                            <DragAndDropSystem
                                handleDrop={DragDropHandleDrop}
                                dragDropRef={DragDropRef}
                                fileError={DragDropFileError}
                                file={DragDropFile}
                                handleUploadFile={DragDropHandleUploadFile}
                            />
                            {(hasStoredResumes && DragDropFile) && (
                                <div style={deleteActionWrapperStyle}>
                                    <button
                                        type="button"
                                        style={deleteButtonStyle}
                                        title="Remove attached resume"
                                        onClick={clearManualUpload}
                                    >
                                        <MdDelete size={20} />
                                    </button>
                                </div>
                            )}
                            {DragDropFileError && (
                                <div style={errorTextStyle}>Upload failed. Try PDF, DOC, or TXT under 14MB.</div>
                            )}
                        </>
                    )}
                    <div className="textareaWrapper">
                        <InputHandler
                            id={"additionalInfo"}
                            type="textBox"
                            name="additionalInfo"
                            value={optimizeAdditionalInfo}
                            placeholder="[Optional] add job description via text or screenshots (up to 5), or tell me to change something in your CV."
                            multiline={true}
                            onChange={(event) => optimizeSetAdditionalInfo(event.target.value)}
                        />
                        {/* Full-width parent for Upload/Paste button below textarea */}
                        <div className="uploadBtnBar">
                            <button
                                onClick={triggerAddlInfoSelect}
                                title="Upload image or paste"
                                className={uploadBtnStyles.uploadPasteBtn}
                                type="button"
                            >
                                <span className={uploadBtnStyles.uploadPasteBtnText}>Upload/Paste</span>
                            </button>
                        </div>
                    </div>
                    {/* Optional image attachments (max 5) */}
                    <div className="optional">
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {Array.isArray(addlInfoImagePreviews) && addlInfoImagePreviews.map((url, idx) => (
                                <div key={idx} style={{ position: 'relative' }}>
                                    <img src={url} alt={`attach-${idx}`} style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #5fbec5' }} />
                                    <button
                                        type="button"
                                        onClick={(e) => removeSingleAddlInfoImage(idx, e)}
                                        style={{ position: 'absolute', top: -6, right: -6, border: 'none', background: 'crimson', color: '#fff', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer' }}
                                        aria-label="Remove attachment"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                        <input ref={addlInfoFileInputRef} type="file" multiple accept="image/*" onChange={handleAddlInfoFileSelect} hidden />
                    </div>
                </div>
                <div className="inputSection">
                    <div
                        style={{
                            ...tipSectionWrapperStyle,
                            cursor: 'pointer',
                            marginTop: hasStoredResumes ? 20 : 0,
                        }}
                        onClick={handleTipBoxToggle}
                        onKeyDown={handleTipKeyDown}
                        role="button"
                        tabIndex={0}
                        aria-pressed={needsCareerGuidance}
                    >
                        <div style={tipHeaderStyle}>
                            <FaRegLightbulb size={isMobile ? 26 : 18} color={highlightYellow} />
                            <span>TIP: Lost on the career path to take? Click the checkbox below.</span>
                        </div>
                        <label
                            style={tipCheckboxWrapperStyle}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {guidanceCheckLoading ? (
                                <OvalSpinner size={18} color={highlightYellow} />
                            ) : (
                                <input
                                    type="checkbox"
                                    checked={needsCareerGuidance}
                                    onChange={handleGuidanceCheckboxChange}
                                    style={needsCareerGuidance ? {...checkboxBaseStyle, ...checkboxCheckedStyle} : checkboxBaseStyle}
                                    aria-label="I need career guidance"
                                    onClick={(event) => event.stopPropagation()}
                                />
                            )}
                            <span>I Need Career Roadmap.</span>
                        </label>
                    </div>
                    {needsCareerGuidance ? (
                        <InputHandler
                            type="textBox"
                            label="Interests / Hobbies"
                            id="guidanceInterests"
                            name="guidanceInterests"
                            value={interestsTextValue}
                            placeholder="List interests or hobbies (comma, newline, or bullet separated)."
                            multiline={true}
                            onChange={handleInterestsTextChange}
                        />
                    ) : (
                        <InputHandler
                            type="text"
                            label="Job Position"
                            id={"basicInfojobPosition"}
                            name={optimizeBasicInfo.jobPosition}
                            value={optimizeBasicInfo.jobPosition}
                            onChange={optimizeHandleInputChange("jobPosition")}

                        />
                    )}

                    <InputHandler
                        type="select"
                        label="Type of Connection"
                        id="jobConnectType"
                        name="jobConnectType"
                        value={optimizeJobConnectType}
                        list={optimizeJobTypesArray}
                        onChange={(event) => optimizeHandleJobConnectType(event)}
                    />
                    <InputHandler
                        type="select"
                        label="Country"
                        id={"basicInfo.country"}
                        name={optimizeBasicInfo.country}
                        value={optimizeBasicInfo.country}
                        onChange={optimizeHandleInputChange("country")}
                        inputType="country-select"

                    />
                    <InputHandler
                        type="select"
                        label="State"
                        id={"basicInfo.city"}
                        country={optimizeBasicInfo.country}
                        name={optimizeBasicInfo.city}
                        value={optimizeBasicInfo.city}
                        onChange={optimizeHandleInputChange("city")}
                        inputType="state-select"
                    />
                </div>

            </div>


            <div className="optimizeBtnRow">
                <Button
                    primary={true}
                    width="100%"
                    title="Optimize Resume"
                    clickHandle={handleClick}
                    
                >
                    <FaWandMagicSparkles />
                </Button>
            </div>

        </PageDataContainerStyled>

    );
}

export default OptimizePageData
